/**
 * Shared Tailwind CSS Class Constants
 * Eliminates repeated class strings across components
 *
 * Usage:
 * import { TABLE_STYLES, BUTTON_STYLES, STATUS_BADGE_STYLES } from '@/constants/styles';
 *
 * <th className={TABLE_STYLES.header}>Name</th>
 * <td className={TABLE_STYLES.cell}>Value</td>
 * <button className={BUTTON_STYLES.iconButton}>...</button>
 */

// ===========================================
// Table Styles
// ===========================================

export const TABLE_STYLES = {
    /** Table header cell (left-aligned) */
    th: 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase',

    /** Table header cell centered */
    thCenter: 'px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase',

    /** Table header cell right-aligned */
    thRight: 'px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase',

    /** Table data cell */
    td: 'px-4 py-4',

    /** Table data cell centered */
    tdCenter: 'px-4 py-4 text-center',

    /** Table row with hover */
    row: 'hover:bg-gray-50',

    /** Table row clickable */
    rowClickable: 'hover:bg-gray-50 cursor-pointer',

    /** Table container */
    container: 'overflow-x-auto',

    /** Table wrapper */
    table: 'w-full',

    /** Table head */
    thead: 'bg-gray-50',

    /** Table body */
    tbody: 'divide-y divide-gray-200',
} as const;

// ===========================================
// Responsive table helpers
// ===========================================

/** Tailwind breakpoint below which a secondary table column is hidden. */
export type HideBelowBreakpoint = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Cell classes that hide a column on viewports narrower than the
 * breakpoint, so a wide admin table keeps its primary column and its
 * row actions on a phone instead of scrolling several screens sideways.
 * Spelled out in full so Tailwind's scanner emits every variant.
 */
export const HIDE_CELL_BELOW: Record<HideBelowBreakpoint, string> = {
    sm: 'hidden sm:table-cell',
    md: 'hidden md:table-cell',
    lg: 'hidden lg:table-cell',
    xl: 'hidden xl:table-cell',
};

/**
 * The inverse: classes that show an element only on the viewports where
 * a `hideBelow` column of that breakpoint is hidden, so the column's
 * value can sit under the row's primary cell instead of being lost.
 */
export const SHOW_CELL_BELOW: Record<HideBelowBreakpoint, string> = {
    sm: 'sm:hidden',
    md: 'md:hidden',
    lg: 'lg:hidden',
    xl: 'xl:hidden',
};

/** Narrowest first, so the widest breakpoint of a set can be found. */
export const HIDE_BELOW_BREAKPOINTS: readonly HideBelowBreakpoint[] = ['sm', 'md', 'lg', 'xl'];

/**
 * A table whose rows stack into blocks below `md` (one "card" per row,
 * every cell on its own line) and lay out as a normal table from `md`
 * up. `labelledCell` prints the cell's `data-label` attribute in front
 * of the value while stacked, for values that are ambiguous on their own.
 */
const STACKED_CELL = 'block md:table-cell';
const STACKED_CELL_LABEL = 'before:mr-2 before:text-xs before:uppercase before:tracking-wider before:text-gray-400 before:content-[attr(data-label)] md:before:content-none';

export const STACKED_TABLE = {
    table: 'block w-full md:table',
    thead: 'hidden md:table-header-group',
    tbody: 'block md:table-row-group',
    row: 'block md:table-row',
    cell: STACKED_CELL,
    labelledCell: `${STACKED_CELL} ${STACKED_CELL_LABEL}`,
    /** The label alone, for a cell that needs its own display rule (e.g. a column that also hides between `md` and `xl`). */
    cellLabel: STACKED_CELL_LABEL,
} as const;

// ===========================================
// Button Styles
// ===========================================

export const BUTTON_STYLES = {
    /** Primary button */
    primary: 'btn-primary inline-flex items-center gap-2',

    /** Secondary button */
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors',

    /** Danger button */
    danger: 'inline-flex items-center gap-2 bg-red-100 text-red-700 hover:bg-red-200 px-4 py-2 rounded-lg transition-colors',

    /** Success button */
    success: 'bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-lg transition-colors',

    /** Warning button */
    warning: 'bg-orange-100 text-orange-700 hover:bg-orange-200 px-4 py-2 rounded-lg transition-colors',

    /** Icon button (table actions) */
    iconButton: 'p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors',

    /** Icon button primary hover */
    iconButtonPrimary: 'p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors',

    /** Icon button blue hover */
    iconButtonBlue: 'p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors',

    /** Icon button green/success hover */
    iconButtonGreen: 'p-2 text-gray-500 hover:text-green-600 hover:bg-gray-100 rounded-lg transition-colors',

    /** Icon button orange/warning hover */
    iconButtonOrange: 'p-2 text-gray-500 hover:text-orange-600 hover:bg-gray-100 rounded-lg transition-colors',

    /** Icon button danger hover */
    iconButtonDanger: 'p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors',
} as const;

// ===========================================
// Status Badge Styles
// ===========================================

export const STATUS_BADGE_STYLES = {
    /** Base badge styling */
    base: 'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',

    /** Status colors */
    colors: {
        green: 'bg-green-100 text-green-700',
        red: 'bg-red-100 text-red-700',
        yellow: 'bg-yellow-100 text-yellow-700',
        blue: 'bg-blue-100 text-blue-700',
        orange: 'bg-orange-100 text-orange-700',
        gray: 'bg-gray-100 text-gray-700',
        purple: 'bg-purple-100 text-purple-700',
    },
} as const;

// ===========================================
// Centralized Status Colors
// Single source of truth for all status-to-color mappings
// ===========================================

export const STATUS_COLORS = {
    // General status
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-700',
    pending: 'bg-yellow-100 text-yellow-700',
    suspended: 'bg-red-100 text-red-700',
    banned: 'bg-red-100 text-red-700',
    deleted: 'bg-red-100 text-red-700',

    // Agreement status
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    viewed: 'bg-yellow-100 text-yellow-700',
    signed: 'bg-green-100 text-green-700',
    pending_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700',
    sent_to_dotloop: 'bg-blue-100 text-blue-700',
    dotloop_send_failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-red-100 text-red-700',

    // Contact exchange status
    contacted: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',

    // Email status
    failed: 'bg-red-100 text-red-700',

    // Appointment request status
    new: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',

    // Property status
    for_sale: 'bg-green-100 text-green-700',
    sold: 'bg-gray-100 text-gray-700',
    off_market: 'bg-gray-100 text-gray-700',

    // Open house phase (derived server-side). `cancelled` is shared with
    // the agreement status above.
    upcoming: 'bg-blue-100 text-blue-700',
    live: 'bg-green-100 text-green-700',
    sign_in_open: 'bg-orange-100 text-orange-700',
    closed: 'bg-gray-100 text-gray-700',

    // Verification
    verified: 'bg-green-100 text-green-700',
    unverified: 'bg-yellow-100 text-yellow-700',
} as const;

export type StatusKey = keyof typeof STATUS_COLORS;

/**
 * Solid variant colors for property badges (high contrast)
 */
export const PROPERTY_BADGE_COLORS = {
    active: 'bg-green-500 text-white',
    available: 'bg-blue-500 text-white',
    pending: 'bg-yellow-500 text-white',
    sold: 'bg-red-500 text-white',
    for_sale: 'bg-green-500 text-white',
    off_market: 'bg-gray-500 text-white',
} as const;

export type PropertyBadgeKey = keyof typeof PROPERTY_BADGE_COLORS;

/**
 * Get status color class by status key (light variant)
 * Falls back to gray if status not found
 */
export function getStatusColor(status?: string | null): string {
    if (!status) return STATUS_COLORS.inactive;
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_') as StatusKey;
    return STATUS_COLORS[normalizedStatus] || STATUS_COLORS.inactive;
}

/**
 * Get property badge color class (solid variant for high contrast)
 * Used for property cards where higher contrast is needed
 */
export function getPropertyBadgeColor(status?: string | null): string {
    if (!status) return PROPERTY_BADGE_COLORS.available;
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_') as PropertyBadgeKey;
    return PROPERTY_BADGE_COLORS[normalizedStatus] || PROPERTY_BADGE_COLORS.available;
}

/**
 * Get user/contact status color classes
 * Alias for getStatusColor - kept for backwards compatibility
 */
export function getUserStatusColor(status?: string | null): string {
    return getStatusColor(status);
}

/**
 * Get full status badge class (base + color)
 */
export function getStatusBadgeClass(status?: string | null): string {
    return `${STATUS_BADGE_STYLES.base} ${getStatusColor(status)}`;
}

// ===========================================
// Avatar/Icon Circle Styles
// ===========================================

export const AVATAR_STYLES = {
    /** Small avatar (32x32) */
    sm: 'w-8 h-8 rounded-full flex items-center justify-center',

    /** Medium avatar (40x40) */
    md: 'w-10 h-10 rounded-full flex items-center justify-center',

    /** Large avatar (48x48) */
    lg: 'w-12 h-12 rounded-full flex items-center justify-center',

    /** Background colors */
    colors: {
        primary: 'bg-primary/20',
        gray: 'bg-gray-100',
        blue: 'bg-blue-100',
        green: 'bg-green-100',
        red: 'bg-red-100',
        purple: 'bg-purple-100',
    },
} as const;

// ===========================================
// Card Styles
// ===========================================

export const CARD_STYLES = {
    /** Standard card */
    base: 'bg-white rounded-xl shadow-sm',

    /** Card with padding */
    padded: 'bg-white rounded-xl shadow-sm p-6',

    /** Card section header */
    sectionHeader: 'text-lg font-semibold text-gray-900 mb-4',
} as const;

// ===========================================
// Form Input Styles
// ===========================================

export const INPUT_STYLES = {
    /** Text input */
    text: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent',

    /** Textarea */
    textarea: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none',

    /** Select */
    select: 'px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent',

    /** Label */
    label: 'block text-sm font-medium text-gray-700 mb-1',
} as const;

// ===========================================
// Layout Styles
// ===========================================

/**
 * Root class for an admin page that fills the content area with its own
 * internal scrolling (Communication Center, Call History).
 *
 * AdminLayout's `<main>` is a bounded flex column (the viewport minus the
 * fixed 4rem header) whose scrolling is its own, so the page takes
 * whatever is left under the call banner and the main padding — no
 * viewport arithmetic, and nothing rendered above it can push the
 * composer or the auto-reply toggle below the fold.
 */
export const ADMIN_FULL_HEIGHT_PAGE = 'flex flex-col flex-1 min-h-0 overflow-hidden';

export const LAYOUT_STYLES = {
    /** Page container */
    page: 'space-y-6',

    /** Grid 2 columns */
    grid2: 'grid grid-cols-1 md:grid-cols-2 gap-4',

    /** Grid 3 columns */
    grid3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',

    /** Grid 4 columns */
    grid4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4',

    /** Flex row with gap */
    flexRow: 'flex items-center gap-2',

    /** Flex between */
    flexBetween: 'flex items-center justify-between',
} as const;

// ===========================================
// Text Styles
// ===========================================

export const TEXT_STYLES = {
    /** Page title */
    pageTitle: 'text-2xl font-bold text-gray-900',

    /** Page subtitle */
    pageSubtitle: 'text-gray-500 mt-1',

    /** Section title */
    sectionTitle: 'text-lg font-semibold text-gray-900',

    /** Body text */
    body: 'text-gray-600',

    /** Small text */
    small: 'text-sm text-gray-500',

    /** Extra small text */
    xs: 'text-xs text-gray-400',

    /** Link */
    link: 'text-primary hover:underline',
} as const;
